import React, { createRef, PureComponent, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { __RouterContext } from 'react-router';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var ATLAS_TRACKING_ELEMENT_ID = 'atlas-tracking';
var DEFAULT_TRACKING_HTML_URL = '//' + location.hostname + '/static/atlas/tracking.rev-d097ff0.html';
/**
 * @param base - base url
 * @param location - location of react-router
 * @param location.pathname - pathname
 * @param location.search - search query
 * @param location.hash - hash
 * @return - url
 */
var buildUrl = function (base, _a) {
    var pathname = _a.pathname, search = _a.search, hash = _a.hash;
    return "" + base + pathname + (search ? "?" + search : '') + (hash ? "#" + hash : '');
};
/**
 * React component for AtlasTrackingJS
 */
var Atlas = /** @class */ (function (_super) {
    __extends(Atlas, _super);
    function Atlas(props) {
        var _this = _super.call(this, props) || this;
        /** postMessage の送信先オリジン */
        _this.origin = location.protocol + "//" + location.host;
        /**
         * constructor -> iframe の onload までに発生した postMessage のペイロードのキュー。
         * iframe ロード後にキューを開放する。
         */
        _this.queue = [];
        /** atlasTracking が利用可能か */
        _this.trackingReady = false;
        _this.iframeRef = createRef();
        window.addEventListener('message', _this);
        _this.handleLoad = _this.handleLoad.bind(_this);
        return _this;
    }
    Atlas.prototype.componentDidMount = function () {
        // <Router /> でラップされているかチェック
        if (this.context === undefined) {
            throw new Error("Don't use <Atlas /> outside a <Router />");
        }
        this.prevLocation = this.context.location;
        this.debug();
    };
    Atlas.prototype.componentDidUpdate = function (prevProps) {
        var currentUrl = buildUrl(this.origin, this.context.location);
        var prevUrl = buildUrl(this.origin, this.prevLocation);
        if (this.props.provideReferrer && prevUrl !== currentUrl) {
            Object.defineProperty(document, 'referrer', {
                value: prevUrl,
                configurable: true,
            });
        }
        if (this.props.debug !== prevProps.debug) {
            this.debug();
        }
        this.prevLocation = this.context.location;
    };
    /**
     * Callback function to be invoked when received message event
     *
     * @param evt - Message Event
     */
    Atlas.prototype.handleEvent = function (evt) {
        if (evt.type !== 'message' ||
            !evt.data ||
            !evt.data.type ||
            evt.origin !== this.origin) {
            return;
        }
        // If tracking iframe haven't loaded yet, stack to queue
        if (!this.trackingReady) {
            this.queue.push(evt.data);
            return;
        }
        this.sendMessage(evt.data);
    };
    /**
     * Callback function to be invoked when iframe is loaded
     */
    Atlas.prototype.handleLoad = function () {
        var _this = this;
        this.queue = this.queue.reduce(function (prev, next) {
            _this.sendMessage(next);
            return prev;
        }, []);
        this.trackingReady = true;
    };
    Atlas.prototype.render = function () {
        var styles = {
            width: '10px',
            height: '10px',
            margin: '-10px',
            position: 'absolute',
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
        };
        return (React.createElement("iframe", { ref: this.iframeRef, src: this.props.trackingHtmlUrl, id: "atlas-tracking", scrolling: "no", frameBorder: "0", title: "Atlas", style: styles, onLoad: this.handleLoad }));
    };
    /**
     * @param message - payload
     */
    Atlas.prototype.sendMessage = function (message) {
        var tracking = this.iframeRef.current;
        if (tracking === null || tracking.contentWindow === null) {
            return;
        }
        tracking.contentWindow.postMessage(message, this.origin);
    };
    Atlas.prototype.debug = function () {
        window.postMessage({
            type: 'atlas.setDebug',
            payload: {
                debug: this.props.debug,
            },
        }, this.origin);
    };
    Atlas.propTypes = {
        provideReferrer: PropTypes.bool,
        trackingHtmlUrl: PropTypes.string,
    };
    Atlas.defaultProps = {
        provideReferrer: false,
        trackingHtmlUrl: DEFAULT_TRACKING_HTML_URL,
    };
    Atlas.contextType = __RouterContext;
    return Atlas;
}(PureComponent));
function getAtlasCookie(name, _a) {
    if (name === void 0) { name = 'NID-Serial-Cookie'; }
    var _b = _a === void 0 ? {} : _a, _c = _b.verbose, verbose = _c === void 0 ? false : _c, _d = _b.targetWindow, targetWindow = _d === void 0 ? window.parent : _d;
    // Some environments throws error due to cross-origin access.
    // So we should prevent raise error because if React component catches this error,
    // it makes stop its lifecycle, maybe...
    try {
        return invokeAtlasTracking('getCookieValue', name);
    }
    catch (err1) {
        verbose && console.log(err1);
        try {
            // Or parse from cookie manually
            var matches = targetWindow.document.cookie.match(new RegExp(name + "=([^;]+)\b?"));
            return matches ? matches[1] : '';
        }
        catch (err2) {
            verbose && console.log(err2);
            return '';
        }
    }
}
function getAtlasLocalStorage(name, _a) {
    var _b;
    if (name === void 0) { name = ''; }
    var _c = _a === void 0 ? {} : _a, _d = _c.verbose, verbose = _d === void 0 ? false : _d, _e = _c.targetWindow, targetWindow = _e === void 0 ? window.parent : _e;
    try {
        return invokeAtlasTracking('getLocalStorageValue', name);
    }
    catch (err1) {
        verbose && console.log(err1);
        try {
            return (_b = targetWindow.localStorage.getItem(name)) !== null && _b !== void 0 ? _b : '';
        }
        catch (err2) {
            verbose && console.log(err2);
            return '';
        }
    }
}
function invokeAtlasTracking(name) {
    var _a;
    var args = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        args[_i - 1] = arguments[_i];
    }
    var tracking = document.getElementById(ATLAS_TRACKING_ELEMENT_ID);
    assertIsIframeElement(tracking);
    var atlasTracking = (_a = tracking.contentWindow) === null || _a === void 0 ? void 0 : _a.atlasTracking;
    if (atlasTracking == null) {
        throw new Error('atlasTracking is NOT present.');
    }
    try {
        // @ts-expect-error
        return atlasTracking[name].apply(atlasTracking, args);
    }
    catch (err) {
        throw new Error("Invoking failed. \"" + err.message + "\"" /* , { cause: err } */);
    }
}
function assertIsIframeElement(element) {
    if (element === null || element.tagName.toLowerCase() !== 'iframe') {
        throw new Error('element is NOT HTMLIFrameElement.');
    }
}

var AtlasContext = /** @class */ (function () {
    function AtlasContext() {
        /**
         * postMessage の送信先 origin
         */
        this.targetOrigin = location.protocol + "//" + location.host;
    }
    /**
     * ページビューを計測する
     *
     * @param params.config - atlas-tracking-js の config に渡されるオブジェクト
     * @param params.payload - initPage に渡されるオブジェクト
     */
    AtlasContext.prototype.trackPage = function (params) {
        var config = params.config, payload = params.payload;
        window.postMessage({ type: 'config', payload: config }, this.targetOrigin);
        window.postMessage({ type: 'initPage', payload: payload }, this.targetOrigin);
    };
    /**
     * ユーザーアクションを計測する
     *
     * @param params.payload - trackAction に渡されるオブジェクト
     */
    AtlasContext.prototype.trackAction = function (params) {
        var payload = params.payload;
        window.postMessage({ type: 'trackAction', payload: payload }, this.targetOrigin);
    };
    return AtlasContext;
}());

var AtlasHooksContext = React.createContext(null);
/**
 * Atlas hooks を使うためのプロバイダー。 <Atlas /> コンポーネントを内包している。
 *
 * @param props
 */
var AtlasHooksProvider = React.memo(function (props) {
    props.children; var atlasProps = __rest(props, ["children"]);
    var atlasContext = useMemo(function () { return new AtlasContext(); }, []);
    return (React.createElement(AtlasHooksContext.Provider, { value: atlasContext },
        props.children,
        React.createElement(Atlas, __assign({}, atlasProps))));
});
/**
 * AtlasHooksContext にアクセスするためのフック
 */
var useAtlas = function () {
    var atlas = useContext(AtlasHooksContext);
    if (atlas === null) {
        throw new Error("Don't use `useAtlas()` outside a <AtlasHooksProvider />");
    }
    return atlas;
};

var noop = function () { };
/**
 * テスト用の <AtlasHooksProvider />。 trackPage, trackAction をモックできるように Props から注入できる。
 *
 * @param {Object} [props]
 * @param {import('react').ReactNode} [props.children] - 子要素
 * @param {() => void} [props.trackPage] - trackPage
 * @param {() => void} [props.trackPage] - trackPage
 *
 * @example
 * const trackAction = jest.fn()
 * const { getByTestId } = render(
 *   <TestAtlasHooksProvider trackAction={trackAction}>
 *     <Banner />
 *   </TestAtlasHooksProvider>
 * )
 *
 * // some operation. e.g, click CTA button.
 * fireEvent.click(getByTestId('cta-button'))
 *
 * expect(trackAction).toHaveBeenCalled()
 */
var TestAtlasHooksProvider = function (props) {
    var children = props.children, _a = props.trackPage, trackPage = _a === void 0 ? noop : _a, _b = props.trackAction, trackAction = _b === void 0 ? noop : _b;
    var context = {
        trackPage: trackPage,
        trackAction: trackAction
    };
    return (React.createElement(AtlasHooksContext.Provider, { value: context }, children));
};

export { Atlas, AtlasHooksProvider, TestAtlasHooksProvider, AtlasHooksContext as __AtlasHooksContext, assertIsIframeElement, getAtlasCookie, getAtlasLocalStorage, useAtlas };
